import { appConfig } from '@/config/environment'
import { userNavigationItems } from '@/config/userNavigation'
import type { PermissionedMenuItem } from '@/types/permission'
import { getCurrentTenantCode } from '@/utils/tenantRoute'

/**
 * 北极星埋点平台 SDK 接入。
 *
 * index.html 中已异步加载 point-sdk.min.js（stub 队列机制），此处负责在
 * 应用启动时生成菜单映射并完成 init；未配置 serverUrl / appKey 时静默跳过，
 * SDK 加载失败时 index.html 的 onerror 兜底会降级为 no-op，均不影响业务。
 */

export interface PointTrackerMenuEntry {
  code: string
  name: string
}

export type PointTrackerMenuMapping = Record<string, PointTrackerMenuEntry>

export interface PointTrackerInitConfig {
  appKey: string
  serverUrl: string
  /** 菜单识别方式二选一：menuMapping（路由映射）与 menuSelector（CSS 选择器） */
  menuMapping?: PointTrackerMenuMapping
  menuSelector?: string
  /** storage 中保存用户信息的 key */
  userInfoKey: string
  /** 用户唯一标识字段名 */
  userIdField: string
  /** 组织字段名（可选） */
  orgField?: string
  /** 岗位字段名（可选） */
  positionField?: string
  debug?: boolean
  batchSize?: number
  batchInterval?: number
}

export interface PointTrackerApi {
  init(config: PointTrackerInitConfig): void
  track(event: string, params?: Record<string, unknown>): void
  setUser(user: unknown, userIdField?: string): void
  setCustomFields(fields: Record<string, unknown>): void
  flush(): void
}

declare global {
  interface Window {
    PointTracker?: PointTrackerApi
  }
}

/**
 * 基于导航菜单配置生成 menuMapping（路由路径 → 菜单编码/名称）。
 *
 * 多租户路径模式下同一菜单同时注册基础路径与 /t/{tenantCode} 前缀变体，
 * 覆盖子域名模式（无前缀）与路径模式两种 URL 形态；activePrefix 一并注册，
 * 保证菜单下的子页面也能命中映射。
 */
export function buildMenuMapping(
  items: PermissionedMenuItem[] = userNavigationItems,
  tenantCode = getCurrentTenantCode()
): PointTrackerMenuMapping {
  const mapping: PointTrackerMenuMapping = {}
  for (const item of items) {
    const paths = new Set<string>()
    if (item.route) paths.add(item.route)
    if (item.path) paths.add(item.path)
    if (item.activePrefix && item.activePrefix !== item.route && item.activePrefix !== item.path) {
      paths.add(item.activePrefix)
    }
    if (paths.size === 0) continue
    const entry: PointTrackerMenuEntry = { code: item.key, name: item.label || item.title }
    for (const path of paths) {
      mapping[path] = entry
      if (tenantCode) {
        mapping[`/t/${tenantCode}${path}`] = entry
      }
    }
  }
  return mapping
}

/**
 * 应用启动时初始化埋点 SDK。
 *
 * 用户信息约定：localStorage / sessionStorage 中 key 为 `user` 的对象，
 * 唯一标识字段为 `username`（与 useAuthSession 写入结构保持一致）。
 */
export function initPointTracker(): void {
  const { trackServerUrl, trackAppKey, trackDebug } = appConfig
  if (!trackServerUrl || !trackAppKey) return
  try {
    window.PointTracker?.init({
      appKey: trackAppKey,
      serverUrl: trackServerUrl,
      menuMapping: buildMenuMapping(),
      userInfoKey: 'user',
      userIdField: 'username',
      debug: trackDebug,
    })
  } catch (error) {
    // SDK 初始化异常不允许影响业务应用启动，记录后继续
    console.warn('[PointTracker] init failed, tracking disabled:', error)
  }
}
